/**
 * LSP ↔ Monaco data conversion, kept free of any `monaco` import so it is unit-testable. Positions: LSP is 0-based
 * (UTF-16 code units), Monaco 1-based. Numeric enums are mapped explicitly.
 */
import type { LspDiagnostic, LspPosition, LspRange } from "./types.ts";

export interface MRange {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}
export const toLspPosition = (p: { lineNumber: number; column: number }): LspPosition => ({
  line: p.lineNumber - 1,
  character: p.column - 1,
});
export const toMonacoRange = (r: LspRange): MRange => ({
  startLineNumber: r.start.line + 1,
  startColumn: r.start.character + 1,
  endLineNumber: r.end.line + 1,
  endColumn: r.end.character + 1,
});
export const toLspRange = (r: MRange): LspRange => ({
  start: { line: r.startLineNumber - 1, character: r.startColumn - 1 },
  end: { line: r.endLineNumber - 1, character: r.endColumn - 1 },
});

/** monaco.MarkerSeverity: Hint 1, Info 2, Warning 4, Error 8. */
export function markerSeverity(lsp: LspDiagnostic["severity"]): 1 | 2 | 4 | 8 {
  switch (lsp) {
    case 2:
      return 4;
    case 3:
      return 2;
    case 4:
      return 1;
    default:
      return 8;
  }
}

/** monaco.MarkerSeverity (numeric) → LSP DiagnosticSeverity. */
export function lspSeverity(monacoSeverity: number): 1 | 2 | 3 | 4 {
  if (monacoSeverity >= 8) return 1;
  if (monacoSeverity >= 4) return 2;
  if (monacoSeverity >= 2) return 3;
  return 4;
}

/** LSP CompletionItemKind (1..25) → monaco.languages.CompletionItemKind (0-based, different order). */
const COMPLETION_KIND: Record<number, number> = {
  1: 18, 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5, 8: 7, 9: 8, 10: 9, 11: 12, 12: 13, 13: 15, 14: 17, 15: 28, 16: 19,
  17: 20, 18: 21, 19: 23, 20: 16, 21: 14, 22: 6, 23: 10, 24: 11, 25: 24,
};
export const completionKind = (k: number | undefined): number => (k !== undefined ? (COMPLETION_KIND[k] ?? 18) : 18);

/** Plain text of LSP documentation / hover contents (string | MarkupContent | MarkedString[]). */
export function markdownOf(c: unknown): string {
  if (c === null || c === undefined) return "";
  if (typeof c === "string") return c;
  if (Array.isArray(c)) return c.map(markdownOf).filter(Boolean).join("\n\n");
  const o = c as { kind?: string; value?: string; language?: string };
  if (typeof o.value === "string") return o.language ? `\`\`\`${o.language}\n${o.value}\n\`\`\`` : o.value;
  return "";
}

/** Diagnostics for a model → Monaco marker data (the SAME store feeds the Problems panel). */
export function toMarkers(diags: readonly LspDiagnostic[]) {
  return diags.map((d) => ({
    ...toMonacoRange(d.range),
    severity: markerSeverity(d.severity),
    message: d.message,
    ...(d.source ? { source: d.source } : {}),
    ...(d.code !== undefined ? { code: String(d.code) } : {}),
    ...(d.tags?.length ? { tags: d.tags } : {}),
  }));
}
